import { LOCALES, type Locale } from '../data/presentation-types';

const FALLBACK_LOCALE: Locale = 'fr-FR';

/**
 * Normalise n'importe quelle chaîne de locale (venant du stockage, du navigateur, ou saisie)
 * vers l'un des 3 codes supportés. Fonction pure, testable isolément. Retombe toujours sur
 * `fr-FR` plutôt que d'échouer — aucune locale non supportée n'est jamais renvoyée.
 */
export function normalizeLocale(value: string | null | undefined): Locale {
  if (!value) return FALLBACK_LOCALE;
  const lowered = value.toLowerCase();
  const exact = LOCALES.find((locale) => locale.toLowerCase() === lowered);
  if (exact) return exact;
  const language = lowered.split('-')[0];
  const byLanguage = LOCALES.find((locale) => locale.toLowerCase().startsWith(`${language}-`));
  return byLanguage ?? FALLBACK_LOCALE;
}

/**
 * Détecte la meilleure locale supportée parmi une liste de préférences navigateur
 * (`navigator.languages`), dans l'ordre. Fonction pure : ne lit jamais `navigator` elle-même,
 * l'appelant fournit la liste — testable sans jsdom ni mock global.
 */
export function detectBrowserLocale(preferredLanguages: readonly string[]): Locale {
  for (const candidate of preferredLanguages) {
    const normalized = normalizeLocale(candidate);
    const lowered = candidate.toLowerCase();
    const language = lowered.split('-')[0];
    const matchesExactly = LOCALES.some((locale) => locale.toLowerCase() === lowered);
    const matchesLanguage = LOCALES.some((locale) => locale.toLowerCase().startsWith(`${language}-`));
    if (matchesExactly || matchesLanguage) return normalized;
  }
  return FALLBACK_LOCALE;
}

/**
 * Synchronise `document.documentElement.lang` avec la locale active — seule écriture DOM
 * autorisée par ce module, documentée ici. Ne fait rien hors navigateur (SSR/tests sans DOM).
 */
export function syncDocumentLocale(locale: Locale, targetDocument: Document | undefined = globalThis.document): void {
  if (!targetDocument?.documentElement) return;
  targetDocument.documentElement.lang = locale;
}
